/**
 * Per-metric lifecycle state — `metricStates` reactive Record keyed by
 * `PhaseId` (3-set: ping, download, upload), plus helpers that read state
 * for a given `MetricId` (4-set: jitter rides ping via `phaseFor`).
 *
 * F-canon rename: `testStates` → `metricStates`, `TestStates` →
 * `MetricStates`. The `TestStates` alias survives in `./types.ts` for the
 * one-wave bridge while consumers migrate.
 */

import { reactive } from "vue";

import { phaseFor, type MetricId, type PhaseId } from "@src/types/speedtest.ts";

import { TestState, type MetricStates } from "./types.ts";

/** Construct the reactive per-phase lifecycle tracker, seeded notStarted. */
export function createMetricStates(): MetricStates {
    return reactive<MetricStates>({
        ping: TestState.notStarted,
        download: TestState.notStarted,
        upload: TestState.notStarted,
    });
}

/** Reset every slot to `notStarted` in place — used by store-level
 *  `resetState` between runs. */
export function resetMetricStates(states: MetricStates): void {
    states.ping = TestState.notStarted;
    states.download = TestState.notStarted;
    states.upload = TestState.notStarted;
}

/** Look up the lifecycle state for a metric. Jitter rides ping. */
export function stateOf(states: MetricStates, metric: MetricId): TestState {
    return states[phaseFor(metric)];
}

export function isStarted(states: MetricStates, metric: MetricId): boolean {
    return stateOf(states, metric) === TestState.started;
}

export function isFinished(states: MetricStates, metric: MetricId): boolean {
    return stateOf(states, metric) === TestState.finished;
}

export function isActive(states: MetricStates, metric: MetricId): boolean {
    return stateOf(states, metric) === TestState.active;
}

export function isActiveOrStarted(states: MetricStates, metric: MetricId): boolean {
    const s = stateOf(states, metric);
    return s === TestState.started || s === TestState.active;
}

/** Re-export the canonical `phaseFor` so consumers reading `metric-state.ts`
 *  pull the helper from one place rather than threading two imports. */
export { phaseFor };
export type { MetricId, PhaseId };
